/**
 * Brazil interpreter version selection modal: CPython and NodeJS version
 * dropdowns with deployment options info.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React from 'react';
import { Box, Button, Checkbox, Container, Modal, Select, SpaceBetween } from '@amzn/awsui-components-core';

interface BrazilVersionModalProps {
  visible: boolean;
  onDismiss: () => void;
  selectedBrazilPipelines: Set<string>;
  selectedBrazilVersions: { cpython?: string; nodejs?: string };
  setSelectedBrazilVersions: React.Dispatch<React.SetStateAction<{ cpython?: string; nodejs?: string }>>;
  onStartUpdate: () => void;
}

const CPYTHON_OPTIONS = [
  { value: 'CPython310', label: 'CPython 3.10 (live)(approaching EOL)' },
  { value: 'CPython311', label: 'CPython 3.11(Recommended)' },
  { value: 'CPython312', label: 'CPython 3.12(Not fully supported)' }
];

const NODEJS_OPTIONS = [
  { value: 'NodeJS-20.x', label: 'NodeJS 20.x (live)(approaching EOL)' },
  { value: 'NodeJS-22.x', label: 'NodeJS 22.x' },
  { value: 'NodeJS-24.x', label: 'NodeJS 24.x(Recommended)' }
];

export const BrazilVersionModal: React.FC<BrazilVersionModalProps> = ({
  visible,
  onDismiss,
  selectedBrazilPipelines,
  selectedBrazilVersions,
  setSelectedBrazilVersions,
  onStartUpdate
}) => (
  <Modal
    visible={visible}
    onDismiss={onDismiss}
    header="🚀 Select Target Interpreter Versions"
    size="medium"
    footer={
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onDismiss}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onStartUpdate}>
            Start Update
          </Button>
        </SpaceBetween>
      </Box>
    }
  >
    <SpaceBetween size="l">
      <Box>Select the target interpreter versions for the selected Brazil pipelines.</Box>

      {/* CPython Version Selector */}
      <Container>
        <SpaceBetween size="s">
          <Box fontWeight="bold">🐍 CPython Interpreter ({selectedBrazilPipelines.size} pipelines)</Box>
          <Select
            selectedOption={selectedBrazilVersions.cpython ? { value: selectedBrazilVersions.cpython, label: selectedBrazilVersions.cpython } : null}
            onChange={({ detail }) => {
              setSelectedBrazilVersions((prev) => ({ ...prev, cpython: detail.selectedOption?.value || '' }));
            }}
            options={CPYTHON_OPTIONS}
            placeholder="Select CPython version"
          />
        </SpaceBetween>
      </Container>

      {/* NodeJS Version Selector */}
      <Container>
        <SpaceBetween size="s">
          <Box fontWeight="bold">📦 NodeJS Interpreter ({selectedBrazilPipelines.size} pipelines)</Box>
          <Select
            selectedOption={selectedBrazilVersions.nodejs ? { value: selectedBrazilVersions.nodejs, label: selectedBrazilVersions.nodejs } : null}
            onChange={({ detail }) => {
              setSelectedBrazilVersions((prev) => ({ ...prev, nodejs: detail.selectedOption?.value || '' }));
            }}
            options={NODEJS_OPTIONS}
            placeholder="Select NodeJS version"
          />
        </SpaceBetween>
      </Container>

      {/* Deployment Options - disabled for Brazil */}
      <Container>
        <SpaceBetween size="s">
          <Box fontWeight="bold">🔧 Deployment Options</Box>
          <Checkbox checked={false} disabled={true} onChange={() => {}}>
            Deploy to Alpha
            <Box color="text-body-secondary" fontSize="body-s">
              <br />
              Not available for Brazil interpreter updates (direct versionset update)
            </Box>
          </Checkbox>
        </SpaceBetween>
      </Container>

      <Box>
        <strong>Selected Pipelines:</strong> {selectedBrazilPipelines.size}
      </Box>
    </SpaceBetween>
  </Modal>
);
